/**
 * Uygulama genelinde tarayıcının window.confirm / window.alert pencereleri yerine kullanılan onay ve bilgi kartları.
 *
 *   if (!(await appConfirm("Are you sure you want to delete this record?"))) return;
 *   appAlert("Saved successfully.");
 *   appConfirm({ title, message, confirmText, cancelText, tone: "danger" | "warning" | "info" | "success" | "error" })
 *
 * <AppDialogHost /> App içinde bir kez render edilir. Host yoksa (ör. test ortamı) tarayıcı penceresine düşer.
 */
import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import Button from "@mui/material/Button";
import Icon from "@mui/material/Icon";
import Box from "@mui/material/Box";
import { useMaterialUIController } from "context";

let enqueue = null;
const pending = [];

const TR_PATTERN = /[çğıöşüÇĞİÖŞÜ]|\b(lütfen|emin misiniz|silmek|kayıt|hata|başarı|seçin|giriniz|zorunlu|tamam)\b/i;
const ERROR_PATTERN = /(error|fail|could not|couldn't|cannot|can't|unable|invalid|not found|denied|unauthori[sz]ed|hata|başarısız|yapılamadı|alınamadı|bulunamadı|geçersiz|olmadı)/i;
const SUCCESS_PATTERN = /(success|başarı|saved|kaydedildi|created|oluşturuldu|updated|güncellendi|deleted|silindi|sent|gönderildi|completed|tamamlandı|uploaded|yüklendi|copied|kopyalandı)/i;
const WARNING_PATTERN = /(please|lütfen|required|zorunlu|must|gerekli|seçin|select|enter |girin|missing|eksik|at least|en az|only|yalnızca|exceed|aşıyor|already|zaten)/i;
const DANGER_PATTERN = /(delete|remove|sil|kaldır|permanently|kalıcı|deactivate|pasif|cancel this|iptal et)/i;
const CAUTION_PATTERN = /(clear|temizle|discard|overwrite|üzerine yaz|reset|sıfırla|lost|kaybol)/i;

const TONES = {
    danger: { icon: "delete_outline", color: "#e53935", soft: "rgba(229, 57, 53, 0.12)" },
    error: { icon: "error_outline", color: "#e53935", soft: "rgba(229, 57, 53, 0.12)" },
    warning: { icon: "warning_amber", color: "#f59e0b", soft: "rgba(245, 158, 11, 0.14)" },
    success: { icon: "check_circle_outline", color: "#16a34a", soft: "rgba(22, 163, 74, 0.12)" },
    info: { icon: "info_outline", color: "#1A73E8", soft: "rgba(26, 115, 232, 0.12)" },
    question: { icon: "help_outline", color: "#1A73E8", soft: "rgba(26, 115, 232, 0.12)" },
};

const TEXT = {
    en: {
        confirm: "Confirm",
        delete: "Delete",
        cancel: "Cancel",
        ok: "OK",
        titles: {
            danger: "Are you sure?",
            warning: "Please confirm",
            question: "Please confirm",
            error: "Something went wrong",
            success: "Done",
            info: "Information",
            alertWarning: "Attention",
        },
    },
    tr: {
        confirm: "Onayla",
        delete: "Sil",
        cancel: "Vazgeç",
        ok: "Tamam",
        titles: {
            danger: "Emin misiniz?",
            warning: "Onay gerekli",
            question: "Onay gerekli",
            error: "Bir hata oluştu",
            success: "İşlem tamamlandı",
            info: "Bilgi",
            alertWarning: "Uyarı",
        },
    },
};

const toText = (value) => {
    if (value == null) return "";
    if (typeof value === "string") return value;
    if (value instanceof Error) return value.message;
    try {
        return JSON.stringify(value);
    } catch (e) {
        return String(value);
    }
};

const normalize = (kind, input) => {
    const opts = input && typeof input === "object" && !(input instanceof Error) ? { ...input } : { message: toText(input) };
    const message = toText(opts.message);
    const lang = opts.lang || (TR_PATTERN.test(`${opts.title || ""} ${message}`) ? "tr" : "en");
    const t = TEXT[lang];

    let tone = opts.tone;
    if (!tone) {
        if (kind === "confirm") {
            if (DANGER_PATTERN.test(message)) tone = "danger";
            else if (CAUTION_PATTERN.test(message)) tone = "warning";
            else tone = "question";
        } else if (ERROR_PATTERN.test(message)) tone = "error";
        else if (SUCCESS_PATTERN.test(message)) tone = "success";
        else if (WARNING_PATTERN.test(message)) tone = "warning";
        else tone = "info";
    }

    const defaultTitle = kind === "alert" && tone === "warning" ? t.titles.alertWarning : t.titles[tone] || t.titles.info;
    const isDelete = kind === "confirm" && tone === "danger" && /(delete|sil\b|silmek|silinecek|remove|kaldır)/i.test(message);

    return {
        kind,
        tone,
        title: opts.title || defaultTitle,
        message,
        confirmText: opts.confirmText || (kind === "alert" ? t.ok : isDelete ? t.delete : t.confirm),
        cancelText: opts.cancelText || t.cancel,
        choices: opts.choices || [],
    };
};

const open = (kind, input) =>
    new Promise((resolve) => {
        const dialog = { ...normalize(kind, input), resolve };
        if (enqueue) enqueue(dialog);
        else if (typeof window !== "undefined") {
            // Host henüz yoksa eski davranışa düş
            if (kind === "confirm") resolve(window.confirm(dialog.message));
            else if (kind === "choose") resolve(window.confirm(dialog.message) ? dialog.choices[0]?.value ?? null : null);
            else {
                window.alert(dialog.message);
                resolve();
            }
        } else pending.push(dialog);
    });

/** Promise<boolean>: kullanıcı onaylarsa true */
export const appConfirm = (input) => open("confirm", input);

/** Promise<void>: kullanıcı kapatınca çözülür; beklemek zorunlu değil */
export const appAlert = (input) => open("alert", input);

/**
 * Promise<value | null>: birden fazla seçenek; Vazgeç / kapatma null döner
 *   appChoose({ message, tone, choices: [{ label, value, tone }] })
 */
export const appChoose = (input) => open("choose", input);

export function AppDialogHost() {
    const [queue, setQueue] = useState([]);
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    useEffect(() => {
        enqueue = (dialog) => setQueue((q) => [...q, dialog]);
        if (pending.length) setQueue((q) => [...q, ...pending.splice(0)]);
        return () => {
            enqueue = null;
        };
    }, []);

    const current = queue[0];

    const close = (result) => {
        if (!current) return;
        if (current.kind === "choose") current.resolve(result === false ? null : result);
        else current.resolve(current.kind === "confirm" ? result : undefined);
        setQueue((q) => q.slice(1));
    };

    if (!current) return null;

    const tone = TONES[current.tone] || TONES.info;
    const surface = darkMode ? "#131c2e" : "#ffffff";
    const border = darkMode ? "rgba(148, 163, 184, 0.16)" : "#e2e8f0";
    const titleColor = darkMode ? "#f1f5f9" : "#1e293b";
    const textColor = darkMode ? "#94a3b8" : "#64748b";
    // Silme / veri kaybı onaylarında yanlışlıkla Enter ile onaylanmasın: odak Vazgeç te başlar
    const focusCancel = current.kind !== "alert" && (current.tone === "danger" || current.tone === "warning");
    const actionButtonSx = (color) => ({
        textTransform: "none",
        fontWeight: 600,
        fontSize: "0.875rem",
        px: 2.25,
        borderRadius: "8px",
        color: "#ffffff !important",
        backgroundColor: color,
        boxShadow: "none",
        "&:hover": { backgroundColor: color, filter: "brightness(0.92)" },
    });

    return (
        <Dialog
            open
            onClose={() => close(false)}
            maxWidth="xs"
            fullWidth
            aria-labelledby="app-dialog-title"
            aria-describedby="app-dialog-message"
            PaperProps={{
                sx: {
                    borderRadius: "14px",
                    border: `1px solid ${border}`,
                    backgroundColor: surface,
                    backgroundImage: "none",
                    boxShadow: darkMode ? "0 24px 48px rgba(0,0,0,0.55)" : "0 24px 48px rgba(15,23,42,0.18)",
                    m: 2,
                },
            }}
            BackdropProps={{ sx: { backgroundColor: "rgba(15, 23, 42, 0.55)", backdropFilter: "blur(2px)" } }}
        >
            <Box sx={{ p: 3, pb: 2.5, display: "flex", gap: 2, alignItems: "flex-start" }}>
                <Box
                    sx={{
                        flexShrink: 0,
                        width: 44,
                        height: 44,
                        borderRadius: "12px",
                        display: "grid",
                        placeItems: "center",
                        backgroundColor: tone.soft,
                        color: tone.color,
                    }}
                >
                    <Icon sx={{ fontSize: "24px !important" }}>{tone.icon}</Icon>
                </Box>
                <Box sx={{ minWidth: 0, pt: 0.25 }}>
                    <Box
                        id="app-dialog-title"
                        component="h2"
                        sx={{ m: 0, fontSize: "1.0625rem", fontWeight: 700, lineHeight: 1.35, color: titleColor }}
                    >
                        {current.title}
                    </Box>
                    {current.message && (
                        <Box
                            id="app-dialog-message"
                            sx={{
                                mt: 0.75,
                                fontSize: "0.875rem",
                                lineHeight: 1.55,
                                color: textColor,
                                whiteSpace: "pre-line",
                                overflowWrap: "anywhere",
                                maxHeight: "50vh",
                                overflowY: "auto",
                            }}
                        >
                            {current.message}
                        </Box>
                    )}
                </Box>
            </Box>
            <Box
                sx={{
                    px: 3,
                    py: 2,
                    display: "flex",
                    justifyContent: "flex-end",
                    flexWrap: "wrap",
                    gap: 1,
                    borderTop: `1px solid ${border}`,
                    backgroundColor: darkMode ? "rgba(15, 23, 42, 0.35)" : "#f8fafc",
                    borderBottomLeftRadius: "14px",
                    borderBottomRightRadius: "14px",
                }}
            >
                {current.kind !== "alert" && (
                    <Button
                        onClick={() => close(false)}
                        autoFocus={focusCancel}
                        sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: "0.875rem",
                            px: 2,
                            borderRadius: "8px",
                            color: `${titleColor} !important`,
                            border: `1px solid ${border}`,
                            backgroundColor: surface,
                            "&:hover": { backgroundColor: darkMode ? "#172238" : "#f1f5f9" },
                        }}
                    >
                        {current.cancelText}
                    </Button>
                )}
                {current.kind === "choose" ? (
                    current.choices.map((choice) => (
                        <Button
                            key={choice.value}
                            onClick={() => close(choice.value)}
                            sx={actionButtonSx((TONES[choice.tone] || TONES.info).color)}
                        >
                            {choice.label}
                        </Button>
                    ))
                ) : (
                    <Button
                        onClick={() => close(true)}
                        autoFocus={!focusCancel}
                        sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: "0.875rem",
                            px: 2.25,
                            borderRadius: "8px",
                            color: "#ffffff !important",
                            backgroundColor: current.kind === "alert" && current.tone !== "error" ? "#1A73E8" : tone.color,
                            boxShadow: "none",
                            "&:hover": {
                                backgroundColor: current.kind === "alert" && current.tone !== "error" ? "#1662C4" : tone.color,
                                filter: "brightness(0.92)",
                            },
                        }}
                    >
                        {current.confirmText}
                    </Button>
                )}
            </Box>
        </Dialog>
    );
}
